<template>
  <!------------------------------------------
    Case services bar chart for the web version of the congressional state profiles.
  -------------------------------------------->
  <div id="congr-state-web-bars" ref="chart">
    <div id="bar-chart"></div>
  </div>
</template>

<script>
import { scaleLinear, scaleBand } from 'd3-scale'
import { select, selectAll } from 'd3-selection'
import { axisBottom, axisLeft } from 'd3-axis'
import { format } from 'd3-format'

const d3 = { select, selectAll, scaleLinear, scaleBand, axisBottom, axisLeft }

export default {
  name: 'StateProfilesWebBars',

  props: {
    caseData: { type: Array, required: true } // array of objects
  },

  data () {
    return {
      loaded: false,
      chartMargins: { top: 20, right: 30, bottom: 40, left: 230 },
      heightPerBar: 40,
      svgWidth: null,
      svgHeight: null,
      resizeObserver: null,
      chart: null,
      svg: null,
      labelsDiv: null,
      y: null,
      x: null
    }
  },

  computed: {
    chartWidth () { return this.svgWidth === null ? null : this.svgWidth - this.chartMargins.left - this.chartMargins.right },
    chartHeight () { return this.svgHeight === null ? null : this.svgHeight - this.chartMargins.top - this.chartMargins.bottom }
  },

  mounted () {
    this.setDivHeight()
    this.loadChart()
    this.loaded = true
  },

  methods: {
    setDivHeight () {
      // Size the container based on how much data
      const setHeight = this.heightPerBar * this.caseData.length + this.chartMargins.top + this.chartMargins.bottom
      document.getElementById('congr-state-web-bars').style.height = setHeight + 'px'
    },

    loadChart () {
      this.svgWidth = this.$refs.chart.offsetWidth,
      this.svgHeight = this.$refs.chart.offsetHeight
      this.addSvg()
      this.addYAxis()
      this.addXAxis()
      this.addBars()
      this.addAxisLabels()
      this.addDataLabels()
    },

    addSvg () {
      const chart = d3.select("#bar-chart")
      const svg = chart.append("svg")
        .attr('height', this.svgHeight + 'px')
        .attr('width', this.svgWidth + 'px')
      
      this.chart = chart
      this.svg = svg
      return
    },

    addYAxis () {
      const y = d3.scaleBand()
        .domain(this.caseData.map(d => d.text))
        .range([ 0, this.chartHeight])
        .padding(0.2)
      
      this.y = y
      return
    },

    addXAxis () {
      const max = Math.max(...(this.caseData.map(x => x.origData)))

      const x = d3.scaleLinear()
        .domain([0, max + 0.1])
        .range([ 0, this.chartWidth])

      const tickWidth = 150

      const xAxis = d3.axisBottom(x).ticks(this.chartWidth / tickWidth).tickFormat(format('~%')).tickSize(-this.chartHeight)
      
      const xAxisG = this.svg.append("g")
        .attr('class', 'x-axis-g')
        .attr("transform", `translate(${this.chartMargins.left}, ${this.chartMargins.top + this.chartHeight})`)
        .call(xAxis)

      xAxisG.selectAll('line').attr('opacity', 0.1)
      xAxisG.selectAll('path').attr('opacity', 0.1)

      xAxisG.selectAll("text")
          .attr("transform", "translate(-10,0)rotate(-45)")
          .style('font-size', '0.75rem')
          .style("text-anchor", "end");

      this.x = x
      return
    },

    addBars () {
      const x = this.x, y = this.y
      const endCapWidth = 2

      const rectsG = this.svg.append('g')
        .attr('class', 'rects-g')
        .attr("transform", `translate(${this.chartMargins.left}, ${this.chartMargins.top})`)
      
      rectsG.selectAll("rect")
        .data(this.caseData)
        .enter()
        .append("rect")
        .attr("x", x(0) )
        .attr("y", function(d) { return y(d.text) })
        .attr("width", function(d) { return d.italicDataPoint === true ? x(d.origData) - endCapWidth : x(d.origData) })
        .attr("height", function(d) { return d.italicDataPoint === true ? y.bandwidth() * 0.67 : y.bandwidth() })
        .attr("transform", function(d) { return d.italicDataPoint === true ? `translate(0, ${y.bandwidth() / 6})` : 'translate(0,0)' })
        .attr("fill", '#4e79a7')
        .style("opacity", function(d) { return d.italicDataPoint === true ? 0.4 : 1 })

      const endCapsG = this.svg.append('g')
        .attr('class', 'end-caps-g')
        .attr("transform", `translate(${this.chartMargins.left}, ${this.chartMargins.top})`)

      // Endcaps for problem codes
      endCapsG.selectAll("rect")
        .data(this.caseData)
        .enter()
        .append("rect")
        .attr("x", function(d) { return x(d.origData) })
        .attr("y", function(d) { return y(d.text) })
        .attr("width", endCapWidth)
        .attr("transform", function(d) { return d.italicDataPoint === true ? `translate(-${endCapWidth}, -${y.bandwidth() / 6})` : 'translate(0,0)' })
        .attr("height", y.bandwidth())
        .attr("fill", '#4e79a7')
        .style("opacity", function(d) { return d.italicDataPoint === true ? 0.4 : 0 })

      return
    },

    addAxisLabels () {
      // Creating divs for the legal issue labels rather than the y axis D3 ticks, for more flexibility
      const padding = 16
      const x = this.x, 
            y = this.y, 
            w = this.chartMargins.left - padding

      const labelsDiv = this.chart.append('div')
        .attr('id', 'chart-labels')
        .style("top", this.chartMargins.top + 'px')
      
      labelsDiv.selectAll("div")
        .data(this.caseData)
        .enter()
        .append("div")
        .attr('class', 'chart-label')
        .style('position', 'absolute')
        .style("top", function(d) { return y(d.text) + 'px'})
        .style('height', y.bandwidth())
        .style('min-height', y.bandwidth() + 'px')
        .style('max-height', y.bandwidth() + 'px')
        .style('min-width', w + 'px')
        .style('padding-left', function(d) { return d.italicDataPoint === true ? padding * 2 + 'px' : padding + 'px' })
        .style('font-style', function(d) { return d.italicDataPoint === true ? 'italic' : 'none' })
        .text(function (d) { return d.text})
        .attr("x", x(0) )

      this.labelsDiv = labelsDiv
      return
    },

    addDataLabels () {
      const x = this.x, y = this.y, h = this.heightPerBar

      const dataLabelsG = this.svg.append('g')
        .attr('class', 'data-labels-g')
        .attr("transform", `translate(${this.chartMargins.left}, ${this.chartMargins.top})`)
      
      dataLabelsG.selectAll("text")
        .data(this.caseData)
        .enter()
        .append("text")
        .attr('class', 'data-label')
        .text(function(d) { return format(".0%")(d.origData) })   
        .attr("x", function(d) { return x(d.origData) })
        .attr("y", function(d) { return y(d.text) })
        .attr("transform", `translate(12, ${h / 2})` )

      this.svg.append('g')
        .attr('class', 'end-caps-g')
        .attr("transform", `translate(${this.chartMargins.left}, ${this.chartMargins.top})`)

      return
    }
  }

}
</script>

<style lang="scss">
#congr-state-web-bars {
  width: 100%;
  height: 10rem;

  #bar-chart {
    width: 100%;
    height: 100%;
    position: relative;
  }
  #chart-labels {
    position: absolute;

    .chart-label {
      font-size: 1rem;
      display: flex;
      align-items: center;
    }
  }
  .data-label {
    font-size: 0.8rem;
  }
}
</style>